import React, { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { useHistory } from "react-router-dom";
import Project, { projectUrlId } from "../../../models/Project.js";
import moment from "moment";
import DotsActionMenu from "../../DotsActionMenu/index.js";
import classNames from "classnames";
import { useDraggable } from "@dnd-kit/core";
import { getActionsMoveToFolder } from "../utils.js";
import { ProjectCardContainer } from "./styles.js";
import ProjectCoverDialog from "./ProjectCoverDialog";
import ProjectCover from "../ProjectCover";
import EditableTitle from "../EditableTitle";
import { motion } from "framer-motion";
import Utils from "../../../Utils.js";
import { IMPORT_WIP_STEPS } from "../../../../../src/shared/constants.js";
import { useToast } from "../../ToastWrapper/index.js";
import { useProjectStore } from "../../../stores/projectStore.js";
import { AnimateTyping } from "../../Project/AnimateTyping/index.js";
import { AppEventPublisher } from "../../../AppEventPublisher.js";
import { useStickyTourStore } from "../../../stores/stickyTourStore.js";
import StickyNoteAnchor from "../../StickyTour/StickyNoteAnchor.js";
import { AnchorIds, TourSignal } from "../../StickyTour/TourTypes";
import useImportSpeedPerceptionExperiment, { isImportSpeedPerceptionActive } from "../../../hooks/useImportSpeedPerceptionExperiment";

function ExportSubtitle({ label, exportProgress, exportingActive, activeExportMessage }) {
  return (
    <div className="project-subtitle-text">
      <span className="project-subtitle-primary">{label}</span>
      {exportingActive && (
        <div className="export-progress-bar">
          <div className="export-progress-fill" style={{ width: `${exportProgress}%` }} />
        </div>
      )}
      {activeExportMessage && <span className="project-subtitle-subtext">{activeExportMessage}</span>}
    </div>
  );
}

const EXPORT_STATUS_MESSAGES = [
  "Herding scenes into the right order...",
  "Convincing characters to behave on the page...",
  "Polishing chapter headings until they shine...",
  "Persuading the muses to sign off on the draft...",
  "Checking for rogue italics in the wings...",
  "Negotiating with stubborn semicolons...",
  "Asking nicely if the chapters could line up...",
  "Bribing the formatting gods with dark roast...",
  "Convincing plot holes to stay hidden...",
  "Whispering sweet nothings to the page breaks...",
  "Making sure all the cliffhangers are still hanging...",
  "Dusting off forgotten subplots and characters...",
  "Untangling the knotted-up plot threads...",
  "Collecting stray footnotes from between cushions...",
  "Convincing the third act to show up on time...",
  "Ensuring your protagonist remembered their arc...",
];

export default function ProjectCard({
  attachTourAnchor,
  project,
  onMoveToFolder,
  onDelete,
  onRestore,
  onArchiveDelete,
  disabled,
  foldersById,
  foldersByParentId,
  onDuplicate,
  onCancelImport,
  onSetCover,
  onRemoveCover,
  onRename,
  mutate,
  disableWelcomeCard,
}) {
  const history = useHistory();
  const { attributes, listeners, setNodeRef, transform, isDragging, over } = useDraggable({ id: project.id, data: { type: "project" } });
  const [importStep, setImportStep] = useState(null);
  const [hasMyVoiceTraining, setHasMyVoiceTraining] = useState(false);
  const [importReportReadable, setImportReportReadable] = useState(false);
  const [actions, setActions] = useState({});
  const [firebaseProjectLoaded, setFirebaseProjectLoaded] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isMergedExporting, setIsMergedExporting] = useState(false);
  const [exportMessageIndex, setExportMessageIndex] = useState(0);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportJobId, setExportJobId] = useState(null);
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const firebaseProject = useRef(null);
  const { setToast } = useToast();
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const importSpeedPerceptionExperiment = useImportSpeedPerceptionExperiment();
  const importSpeedPerceptionActive = isImportSpeedPerceptionActive(importSpeedPerceptionExperiment, project.id);
  // Title animation state
  const titleAnimation = useProjectStore((state) => state.titleAnimations[project.firebase_id]);
  const completeTitleAnimation = useProjectStore((state) => state.completeTitleAnimation);

  const exportingActive = isExporting || isMergedExporting;

  useEffect(() => {
    if (!exportingActive) {
      return;
    }

    setExportMessageIndex(Math.floor(Math.random() * EXPORT_STATUS_MESSAGES.length));

    const interval = setInterval(() => {
      setExportMessageIndex((prev) => (prev + 1) % EXPORT_STATUS_MESSAGES.length);
    }, 3500);

    return () => clearInterval(interval);
  }, [exportingActive]);

  const activeExportMessage = exportingActive ? EXPORT_STATUS_MESSAGES[exportMessageIndex] : null;

  useEffect(() => {
    if (!project.is_importing) {
      Project.get(project.firebase_id).then((fetchedProject) => {
        firebaseProject.current = fetchedProject;
        setFirebaseProjectLoaded(true);
        // Initialize hasMyVoiceTraining from Firebase data for non-importing projects too
        setHasMyVoiceTraining(fetchedProject?.data?.hasMyVoiceTraining || false);
      });

      return () => {
        firebaseProject.current = null;
        setFirebaseProjectLoaded(false);
      };
    }
  }, [project]);

  useEffect(() => {
    if (project.is_importing) {
      Project.get(project.firebase_id).then((fetchedProject) => {
        firebaseProject.current = fetchedProject;
        setFirebaseProjectLoaded(true);
        setImportStep(fetchedProject?.importStep ?? null);
        setImportReportReadable(!!(fetchedProject?.importReportDocumentId || fetchedProject?.importReportUnavailable));
        setHasMyVoiceTraining(fetchedProject?.hasMyVoiceTraining || false);

        firebaseProject.current?.subscribe((updatedProject) => {
          const nextReadable = !!(updatedProject.importReportDocumentId || updatedProject.importReportUnavailable);
          setImportReportReadable((prev) => (prev === nextReadable ? prev : nextReadable));

          if (importStep !== updatedProject.importStep) {
            setImportStep(updatedProject.importStep);
            mutate();
          }
          if (hasMyVoiceTraining !== updatedProject.hasMyVoiceTraining) {
            setHasMyVoiceTraining(updatedProject.hasMyVoiceTraining || false);
          }
        });
      });

      return () => {
        firebaseProject.current?.release();
        setImportReportReadable(false);
      };
    } else {
      setImportStep(null);
      setImportReportReadable(false);
    }
  }, [project.is_importing, project.firebase_id, mutate, importStep, hasMyVoiceTraining]);

  // Update actions when project data changes
  useEffect(() => {
    const computeActions = () => {
      if (!project.deleted && !project.archived) {
        if (project.is_importing) {
          return {
            cancelImport: {
              label: "Cancel Import",
              danger: true,
              onClick: () => {
                onCancelImport(importSpeedPerceptionActive);
              },
            },
          };
        }

        const docCount = firebaseProject.current?.data?.contents?.contents?.length;

        const exportAction = {
          export: {
            label: docCount ? `Export as ${docCount} docs` : "Export (.zip)",
            icon: (
              <SVG
                width={24}
                height={24}
                src="/assets/export.svg"
                className="export"
                aria-hidden="true"
              />
            ),
            onClick: async () => {
              if (isExporting) {
                return;
              }

              setIsExporting(true);
              setExportProgress(0);
              setExportJobId(null);
              setToast(`Preparing export for "${project.title}"`);

              try {
                await Project.exportProject(
                  project.firebase_id,
                  project.title,
                  (progress) => {
                    setExportProgress(progress);
                  },
                  (jobId) => {
                    setExportJobId(jobId);
                  }
                );
                setToast(`Project "${project.title}" exported`);
              } catch (error) {
                if (error?.isExportCancelled) {
                  console.info("Export cancelled by user", error);
                  setToast("Export cancelled");
                } else {
                  console.error("Failed to export project", error);
                  const errorMessage = error.message || `Failed to export project "${project.title}". Please try again.`;
                  setToast(errorMessage);
                }
              } finally {
                setIsExporting(false);
                setExportProgress(0);
                setExportJobId(null);
              }
            },
            disabled: exportingActive,
          },
          exportMerged: {
            label: "Export as 1 doc",
            icon: (
              <SVG
                width={24}
                height={24}
                src="/assets/export.svg"
                className="export"
                aria-hidden="true"
              />
            ),
            onClick: async () => {
              if (isMergedExporting) {
                return;
              }

              setIsMergedExporting(true);
              setExportProgress(0);
              setExportJobId(null);
              setToast(`Preparing merged export for "${project.title}"`);

              try {
                await Project.exportMergedProject(
                  project.firebase_id,
                  project.title,
                  (progress) => {
                    setExportProgress(progress);
                  },
                  (jobId) => {
                    setExportJobId(jobId);
                  }
                );
                setToast(`Project "${project.title}" exported as merged document`);
              } catch (error) {
                if (error?.isExportCancelled) {
                  setToast("Export cancelled");
                } else {
                  const errorMessage = error.message || `Failed to export merged project "${project.title}". Please try again.`;
                  setToast(errorMessage);
                }
              } finally {
                setIsMergedExporting(false);
                setExportProgress(0);
                setExportJobId(null);
              }
            },
            disabled: exportingActive,
          },
          duplicate: {
            label: "Duplicate",
            icon: (
              <SVG
                width={24}
                height={24}
                src="/assets/duplicate.svg"
                className="duplicate"
                aria-hidden="true"
              />
            ),
            onClick: () => {
              onDuplicate(project.firebase_id);
            },
            disabled: exportingActive,
          },
          changeCover: {
            label: project.cover_image_url ? "Change cover" : "Add cover",
            icon: (
              <SVG
                width={24}
                height={24}
                src="/assets/image.svg"
                className="cover"
                aria-hidden="true"
              />
            ),
            onClick: () => {
              setCoverDialogOpen(true);
            },
            disabled: exportingActive,
          },
          moveToFolder: {
            label: "Move to...",
            icon: (
              <SVG
                width={24}
                height={24}
                src="/assets/move-to.svg"
                className="folder"
                aria-hidden="true"
              />
            ),
            onClick: () => {
              return getActionsMoveToFolder(foldersById, foldersByParentId, onMoveToFolder, project.folder_id, true);
            },
            disabled: exportingActive,
          },
          deleteProject: {
            label: "Delete",
            danger: true,
            icon: (
              <SVG
                width={24}
                height={24}
                src="/assets/delete.svg"
                className="trash"
                aria-hidden="true"
              />
            ),
            onClick: () => {
              onDelete(project);
            },
          },
        };

        if (docCount === 1) {
          // `exportMerged` is better suited for single doc projects
          delete exportAction.export;
        }

        // Add cancel action when exporting
        if (exportingActive) {
          return {
            cancelExport: {
              label: "Cancel Export",
              danger: true,
              icon: (
                <SVG
                  width={24}
                  height={24}
                  src="/assets/close.svg"
                  className="close"
                  aria-hidden="true"
                />
              ),
              disabled: !exportJobId,
              onClick: async () => {
                if (!exportJobId) {
                  setToast("Still preparing export—try again in a moment.");
                  return;
                }
                try {
                  console.log("Cancelling export job:", exportJobId);
                  await Project.cancelExport(exportJobId);
                  setToast("Export cancelled");
                  setIsExporting(false);
                  setIsMergedExporting(false);
                  setExportProgress(0);
                  setExportJobId(null);
                } catch (error) {
                  console.error("Failed to cancel export", error);
                  const errorMessage = error.message || "Failed to cancel export";
                  setToast(errorMessage);
                }
              },
            },
          };
        }

        return exportAction;
      } else if (project.archived) {
        // TODO: Add unarchive action
        return {};
      } else if (project.deleted) {
        return {
          restoreProject: {
            label: "Restore",
            iconStroke: true,
            icon: (
              <SVG
                width={24}
                height={24}
                src="/assets/restore.svg"
                className="restore"
                aria-hidden="true"
              />
            ),
            onClick: () => {
              onRestore(project);
            },
          },
          permanentDeleteProject: {
            label: "Permanently Delete",
            danger: true,
            icon: (
              <SVG
                width={24}
                height={24}
                src="/assets/delete.svg"
                className="trash"
                aria-hidden="true"
              />
            ),
            onClick: () => {
              onArchiveDelete(project);
            },
          },
        };
      }

      return {};
    };

    setActions(computeActions());
  }, [
    project,
    firebaseProjectLoaded,
    isExporting,
    isMergedExporting,
    exportJobId,
    foldersById,
    foldersByParentId,
    onCancelImport,
    onMoveToFolder,
    onRestore,
    onArchiveDelete,
    onDelete,
    onDuplicate,
    setToast,
  ]);

  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  const importBlocksOpen = project.is_importing && (!importSpeedPerceptionActive || !importReportReadable);
  const cardDisabled = disabled || isExporting || isMergedExporting || importBlocksOpen;

  const openProject = (event) => {
    if (cardDisabled) return;
    sendFlowSignal(TourSignal.PROJECT_CARD_CLICKED);
    disableWelcomeCard();
    AppEventPublisher.projects.openProject(firebaseProject.current?.source || project.source || "unknown");
    const url = projectUrlId(project.firebase_id);
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      history.push(url);
    }
  };

  return (
    <>
      <ProjectCardContainer
        as={motion.div}
        ref={importBlocksOpen ? null : setNodeRef}
        style={style}
        {...(importBlocksOpen ? {} : { ...attributes, ...listeners })}
        className={classNames({ disabled: cardDisabled, isDragging, over })}
        aria-label={
          importBlocksOpen
            ? `${project.title} — import preparing, opens when the Manuscript Report is ready`
            : `Open project: ${project.title}`
        }
        onClick={openProject}
        onKeyDown={(event) => {
          if (event.key === "Enter" && event.target === event.currentTarget) {
            event.preventDefault();
            openProject(event);
          }
        }}
        layout={!isDragging ? "position" : false}
      >
        {attachTourAnchor && <StickyNoteAnchor anchorId={AnchorIds.PROJECT_CARD} />}
        <ProjectCover
          imageUrl={project.cover_image_url}
          title={project.title}
          showTitle
          tinted={false}
        />
        <div className="project-title">
          <div className="project-title-row">
            <EditableTitle
              className={classNames("project-title-text", { exporting: exportingActive })}
              title={project.title}
              disabled={cardDisabled || !!project.isDuplicating || !!project.deleted || !!titleAnimation?.isAnimating}
              onRename={(title) => onRename(project, title)}
            >
              {titleAnimation?.isAnimating ? (
                <AnimateTyping
                  oldText={titleAnimation.oldTitle}
                  newText={titleAnimation.newTitle}
                  delay={30}
                  usePreWrapper={false}
                  setDoneAnimating={() => {
                    completeTitleAnimation(project.firebase_id);
                    // Refetch to update with new title from backend
                    mutate();
                  }}
                />
              ) : null}
            </EditableTitle>
            <DotsActionMenu
              actions={actions}
              iconSize={16}
              position="bottom-start"
              size={24}
              ariaLabel={`More options for ${project.title}`}
            />
          </div>
          {(isExporting || isMergedExporting) && (
            <ExportSubtitle
              label="Exporting project..."
              exportProgress={exportProgress}
              exportingActive={exportingActive}
              activeExportMessage={activeExportMessage}
            />
          )}
          {project.isDuplicating && <div className="project-subtitle-text">Duplicating...</div>}
          {project.is_importing && <div className="project-import-text">{IMPORT_WIP_STEPS[importStep] || "Importing..."}</div>}
        </div>
        <div className="project-footer">
          <p className="project-word-count">
            {project.is_importing
              ? (() => {
                  // Filter out TRAINING_MY_VOICE step if user didn't opt for My Voice training
                  const allSteps = Object.keys(IMPORT_WIP_STEPS);
                  const activeSteps = hasMyVoiceTraining ? allSteps : allSteps.filter((step) => step !== "TRAINING_MY_VOICE");

                  return `Step ${activeSteps.findIndex((step) => step === importStep) + 1} of ${activeSteps.length - 1}`;
                })()
              : `${Utils.numberWithCommas(project.words)} words`}
          </p>
          <p className="project-last-edited">{moment(project.last_active).fromNow()}</p>
        </div>
      </ProjectCardContainer>
      <ProjectCoverDialog
        open={coverDialogOpen}
        onOpenChange={setCoverDialogOpen}
        projectTitle={project.title}
        currentCoverUrl={project.cover_image_url || null}
        onSave={(payload) => onSetCover(project, payload)}
        onRemove={() => onRemoveCover(project)}
      />
    </>
  );
}
